import { useState } from "react";
import { UserPlus, Plus, Trash2, Loader2, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, TRADES } from "@/lib/api";
import { Input } from "@/components/ui/input";

const empty = () => ({ id: crypto.randomUUID(), name: "", trade: TRADES[0] });

export function WorkerAccounts() {
  const [rows, setRows] = useState([empty(), empty(), empty()]);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const set = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const valid = rows.filter((r) => r.name.trim());

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/teams/accounts", { workers: valid.map(({ name, trade }) => ({ name, trade })) });
      setCreated(data.accounts);
      setRows([empty(), empty(), empty()]);
      toast.success(`${data.accounts.length} compte(s) créé(s)`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (created)
    return (
      <section className="space-y-3 rise" data-testid="worker-accounts-created">
        <div className="flex items-center justify-between print:hidden">
          <p className="text-xs font-bold uppercase text-slate-500">Cartes de connexion à distribuer</p>
          <button onClick={() => window.print()} data-testid="worker-accounts-print-btn" className="h-10 px-4 rounded-xl bg-orange-500 text-white font-bold flex items-center gap-2"><Printer className="w-4 h-4" /> Imprimer</button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {created.map((a, i) => (
            <div key={a.identifiant} className="certificate bg-white text-slate-900 border-2 border-dashed border-slate-500 rounded-xl p-4 break-inside-avoid" data-testid={`worker-card-${i}`}>
              <div className="font-display text-2xl font-bold">{a.name}</div>
              <div className="text-sm">{a.trade}</div>
              <div className="mt-2 text-sm">Identifiant : <b className="text-lg tracking-wide" data-testid={`worker-card-ident-${i}`}>{a.identifiant}</b></div>
              <div className="text-sm">Code : <b className="text-lg tracking-widest" data-testid={`worker-card-code-${i}`}>{a.code}</b></div>
              <div className="text-[11px] text-slate-500 mt-1">{window.location.origin}/connexion</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-red-700 font-semibold print:hidden">Notez ou imprimez ces codes maintenant : ils ne seront plus affichés.</p>
        <button onClick={() => setCreated(null)} data-testid="worker-accounts-again-btn" className="w-full h-11 rounded-xl border-2 border-slate-300 font-bold print:hidden">Créer d'autres comptes</button>
      </section>
    );

  return (
    <section className="bg-white rounded-2xl border-2 border-slate-200 p-4 space-y-3 rise print:hidden" data-testid="worker-accounts">
      <p className="flex items-center gap-2 text-xs font-bold uppercase text-slate-500"><UserPlus className="w-4 h-4" /> Créer les comptes de l'équipe (sans email)</p>
      {rows.map((r, i) => (
        <div key={r.id} className="flex gap-2">
          <Input value={r.name} onChange={(e) => set(i, "name", e.target.value)} maxLength={40} placeholder="Prénom" className="h-11 flex-1" data-testid={`worker-name-input-${i}`} />
          <select value={r.trade} onChange={(e) => set(i, "trade", e.target.value)} className="h-11 rounded-md border border-input bg-transparent px-2 text-sm" data-testid={`worker-trade-select-${i}`}>
            {TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <button onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label="Retirer" data-testid={`worker-remove-btn-${i}`} className="w-11 h-11 grid place-items-center text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button onClick={() => rows.length < 30 && setRows([...rows, empty()])} data-testid="worker-add-row-btn" className="text-sm font-bold text-orange-600 flex items-center gap-1"><Plus className="w-4 h-4" /> Ajouter une ligne</button>
      <button onClick={submit} disabled={busy || !valid.length} data-testid="worker-accounts-create-btn" className="w-full h-12 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-40">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <UserPlus className="w-5 h-5" />} Créer {valid.length} compte(s)
      </button>
    </section>
  );
}
